import type { SessionUser } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { STR } from '../i18n';
import { ApiError, redeemTwoFactorRecoveryCode, verifyTwoFactorChallenge } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { CHANGE_PASSWORD_PATH, LOGIN_PATH, landingPath } from '../lib/surfaces';
import { showToast } from '../lib/toast';
import { BrandPanel } from '../ui/BrandPanel';
import { Spinner } from '../ui/Primitives';

import { ContinueToNotice } from './LoginPage';

/**
 * THE SIGN-IN CHALLENGE — the login page one step later, in the same split panel. The
 * password verified; the person holds the '2fa-pending' cookie (httpOnly, invisible here)
 * and proves the second factor with a six-digit code, or spends a single-use recovery code.
 *
 * The completion contract is the login page's, verbatim: the wall intercepts first (with
 * the validated destination riding along), then `redirectTo` is honoured, then the landing.
 * Every refusal renders as a full sentence — a wrong code, the rate limiter, and a lapsed
 * pending step each say what happened and what to do, never a wire code.
 */

export function TwoFactorChallengePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The server-validated destination from login, passed back with the code and RE-VALIDATED
  // server-side on every submit — it travelled through the client, so it is untrusted again.
  const redirectParam = searchParams.get('redirect');
  const redirect = redirectParam === null || redirectParam === '' ? undefined : redirectParam;

  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  /** Softens the tone and pins the message while the limiter holds — the login page's rule. */
  const [softFailure, setSoftFailure] = useState(false);

  const codeInputRef = useRef<HTMLInputElement | null>(null);
  const recoveryInputRef = useRef<HTMLInputElement | null>(null);

  /** The shared completion path — the login page's contract, one copy. */
  async function completeSignIn(
    user: SessionUser,
    redirectTo: string | null,
    codesRemaining?: number,
  ) {
    setFormError(null);
    setSoftFailure(false);
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });

    if (user.mustChangePassword) {
      const search =
        redirectTo === null
          ? ''
          : `?${new URLSearchParams({ redirect: redirectTo }).toString()}`;
      navigate(`${CHANGE_PASSWORD_PATH}${search}`, { replace: true });
      return;
    }
    if (redirectTo !== null) {
      window.location.assign(redirectTo);
      return;
    }
    // The recovery nudge, said while it still matters — only on the internal landing; a
    // redirect to a consuming application outlives any toast this page could show.
    if (codesRemaining !== undefined) {
      showToast(STR.twoFactor.challenge.codesRemaining(codesRemaining), 'neutral');
    }
    navigate(landingPath(), { replace: true });
  }

  function handleFailure(error: unknown, kind: 'totp' | 'recovery') {
    if (error instanceof ApiError) {
      if (error.code === 'rate_limited') {
        setSoftFailure(true);
        setFormError(STR.twoFactor.challenge.rateLimited);
        return;
      }
      if (error.code === 'unauthenticated') {
        // The pending step lapsed (or never existed): only signing in again mints another.
        setSoftFailure(true);
        setFormError(STR.twoFactor.challenge.expired);
        return;
      }
      setSoftFailure(false);
      if (error.code === 'invalid_credentials') {
        setFormError(
          kind === 'totp'
            ? STR.twoFactor.challenge.wrongCode
            : STR.twoFactor.challenge.wrongRecovery,
        );
        const field = kind === 'totp' ? codeInputRef.current : recoveryInputRef.current;
        field?.focus();
        field?.select();
        return;
      }
      setFormError(error.message);
      return;
    }
    setSoftFailure(false);
    setFormError(STR.errors.generic);
  }

  const verifyMutation = useMutation({
    mutationFn: () => verifyTwoFactorChallenge(redirect === undefined ? { code } : { code, redirect }),
    onSuccess: (result) => {
      // A verified challenge always answers with the complete shape; a pending one here
      // would be a server contract break, and there is no user to complete a sign-in with.
      if (result.user === undefined) {
        handleFailure(new Error('Unexpected pending response'), 'totp');
        return;
      }
      return completeSignIn(result.user, result.redirectTo);
    },
    onError: (error) => {
      handleFailure(error, 'totp');
    },
  });

  const recoveryMutation = useMutation({
    mutationFn: () =>
      redeemTwoFactorRecoveryCode(
        redirect === undefined ? { code: recoveryCode } : { code: recoveryCode, redirect },
      ),
    onSuccess: (result) =>
      completeSignIn(result.user, result.redirectTo, result.recoveryCodesRemaining),
    onError: (error) => {
      handleFailure(error, 'recovery');
    },
  });

  const busy = verifyMutation.isPending || recoveryMutation.isPending;

  /*
   * A FULL session has no business here — the challenge endpoints refuse its lane
   * structurally — so a signed-in visitor is sent to the landing. Gated on both mutations
   * being untouched: during this page's own successful completion the me cache fills in,
   * and this redirect must not race the explicit navigation (wall, external destination)
   * that the completion path is already making.
   *
   * READ NON-REACTIVELY, from the cache the app shell already owns — deliberately NOT
   * `useSession()`. For the person this screen exists for, the me query sits in ERROR with
   * no data (their '2fa-pending' cookie is structurally refused by GET /api/auth/me — the
   * lane law working); that 401 is this screen's DEFINING state, not something to wait
   * out. Mounting a second observer on that query re-fetches it (`retryOnMount` defaults
   * true for an errored, data-less query — queryObserver.ts `shouldLoadOnMount`), the
   * fetch dispatch flips it back to `pending` (query.ts `fetchState`, data undefined), the
   * app shell reads `session.isLoading` and swaps every route for its loading screen, this
   * page unmounts mid-fetch, the 401 lands, the shell restores the route, the remount
   * repeats the cycle — an unbounded mount/fetch loop hammering /api/auth/me at network
   * round-trip speed. A cache read has no observer, so this page issues NO session request
   * of its own, ever: the only fetches on that key are the shell's own single one and the
   * one explicit invalidate in `completeSignIn` after a successful verify.
   */
  const sessionUser = queryClient.getQueryData<SessionUser>(queryKeys.me);
  if (sessionUser != null && verifyMutation.isIdle && recoveryMutation.isIdle) {
    return <Navigate to={landingPath()} replace />;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (mode === 'totp') {
      if (code.length < 6) {
        setSoftFailure(false);
        setFormError(STR.twoFactor.challenge.emptyCode);
        codeInputRef.current?.focus();
        return;
      }
      setFormError(null);
      verifyMutation.mutate();
      return;
    }
    if (recoveryCode.trim() === '') {
      setSoftFailure(false);
      setFormError(STR.twoFactor.challenge.emptyRecovery);
      recoveryInputRef.current?.focus();
      return;
    }
    setFormError(null);
    recoveryMutation.mutate();
  }

  function swapMode() {
    setMode((previous) => (previous === 'totp' ? 'recovery' : 'totp'));
    if (!softFailure) setFormError(null);
  }

  const fieldInvalid = formError !== null && !softFailure;

  return (
    <BrandPanel>
      <div data-view="challenge">
        <h1 className="f-title">{STR.twoFactor.challenge.title}</h1>
        <p className="lead">
          {mode === 'totp'
            ? STR.twoFactor.challenge.leadTotp
            : STR.twoFactor.challenge.leadRecovery}
        </p>
        <div className="stack">
          {redirect === undefined ? null : <ContinueToNotice redirect={redirect} />}

          {formError === null ? null : (
            <div className={softFailure ? 'msg call-orange' : 'msg call-red'} role="alert">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="stack">
              {mode === 'totp' ? (
                <label className="lbl" htmlFor="challenge-code">
                  <span>{STR.twoFactor.challenge.codeLabel}</span>
                  <input
                    ref={codeInputRef}
                    className={fieldInvalid ? 'field code-input field-over' : 'field code-input'}
                    id="challenge-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    disabled={busy}
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                      if (!softFailure) setFormError(null);
                    }}
                  />
                </label>
              ) : (
                <label className="lbl" htmlFor="challenge-recovery">
                  <span>{STR.twoFactor.challenge.recoveryLabel}</span>
                  <input
                    ref={recoveryInputRef}
                    className={fieldInvalid ? 'field rec-input field-over' : 'field rec-input'}
                    id="challenge-recovery"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="XXXXX-XXXXX"
                    disabled={busy}
                    value={recoveryCode}
                    onChange={(event) => {
                      setRecoveryCode(
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9-]/gu, '')
                          .slice(0, 12),
                      );
                      if (!softFailure) setFormError(null);
                    }}
                  />
                  <span className="hint">{STR.twoFactor.challenge.recoveryHint}</span>
                </label>
              )}
              <div className="actions">
                <button className="prim btn btn-main" type="submit" disabled={busy} aria-busy={busy}>
                  {busy ? <Spinner /> : null}
                  <span>
                    {busy
                      ? STR.twoFactor.challenge.verifying
                      : mode === 'totp'
                        ? STR.twoFactor.challenge.submitTotp
                        : STR.twoFactor.challenge.submitRecovery}
                  </span>
                </button>
                <button type="button" className="tbtn btn-quiet" disabled={busy} onClick={swapMode}>
                  {mode === 'totp'
                    ? STR.twoFactor.challenge.swapToRecovery
                    : STR.twoFactor.challenge.swapToTotp}
                </button>
                <button
                  type="button"
                  className="tbtn btn-quiet"
                  disabled={busy}
                  onClick={() => {
                    // Preserve ?redirect= across the way back, exactly as the guard does.
                    navigate(`${LOGIN_PATH}${location.search}`, { replace: true });
                  }}
                >
                  {STR.twoFactor.challenge.backToSignIn}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <p className="aux">{STR.twoFactor.challenge.aux}</p>
    </BrandPanel>
  );
}
